import React, { FC, useMemo } from 'react';
import { object, string } from 'yup';
import { Formik } from 'formik';
import { FormikOnSubmit } from 'lib/formik.utils';
import { Form, FormItem, Input } from 'formik-antd';
import {
  defaultFormLayout,
  defaultFormTailLayout,
} from 'common/config/formLayout';
import { Form as AntForm } from 'antd';
import { IntlMessage, useFormatMessage } from 'common/hooks/useFormatMessage';
import { SaveButton } from 'common/components/SaveButton';

const validationSchemaFactory = (intlMessage: IntlMessage) =>
  object().shape({
    serialNumber: string()
      .required(
        intlMessage(
          { id: 'validation.required' },
          {
            field: intlMessage({ id: 'deviceSerialNumber' }),
          }
        )
      )
      .test(
        'hasLeadingTrailingWhitespace',
        () => intlMessage({ id: 'validation.whitespace' }),
        (value) => value?.trim().length === value?.length
      )
      .max(
        255,
        intlMessage(
          { id: 'validation.maxLength' },
          {
            field: intlMessage({ id: 'deviceSerialNumber' }),
            length: 255,
          }
        )
      ),
    boxSerialNumber: string()
      .test(
        'hasLeadingTrailingWhitespace',
        () => intlMessage({ id: 'validation.whitespace' }),
        (value) => value?.trim().length === value?.length
      )
      .required(
        intlMessage(
          { id: 'validation.required' },
          {
            field: intlMessage({ id: 'devices.boxSerialNumber' }),
          }
        )
      )
      .max(
        255,
        intlMessage(
          { id: 'validation.maxLength' },
          {
            field: intlMessage({ id: 'devices.boxSerialNumber' }),
            length: 255,
          }
        )
      ),
  });

export interface RegisterNewDeviceFormModel {
  serialNumber: string;
  boxSerialNumber: string;
}

const initialValues = {
  serialNumber: '',
  boxSerialNumber: '',
};

export interface RegisterNewDeviceFormProps {
  onSubmit: FormikOnSubmit<RegisterNewDeviceFormModel>;
  loading?: boolean;
}

export const RegisterNewDeviceForm: FC<RegisterNewDeviceFormProps> = (
  props
) => {
  const intlMessage = useFormatMessage();
  const validationSchema = useMemo(() => validationSchemaFactory(intlMessage), [
    intlMessage,
  ]);
  return (
    <Formik
      validationSchema={validationSchema}
      initialValues={initialValues}
      {...props}
    >
      <Form {...defaultFormLayout}>
        <FormItem
          label={intlMessage({ id: 'deviceSerialNumber' })}
          name="serialNumber"
          required
        >
          <Input name="serialNumber" />
        </FormItem>
        <FormItem
          label={intlMessage({ id: 'devices.boxSerialNumber' })}
          name="boxSerialNumber"
          required
        >
          <Input name="boxSerialNumber" />
        </FormItem>

        <AntForm.Item {...defaultFormTailLayout}>
          <SaveButton
            id="button-save-device"
            loading={props.loading ?? false}
          />
        </AntForm.Item>
      </Form>
    </Formik>
  );
};
